import React from "react";
import { AlertStatus } from "../../../types/api/AlertTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import Form from "react-bootstrap/Form";

interface NewReviewProps {
    comment: string,
    setComment(newComment: string): void,
    newStatus: AlertStatus,
    setNewStatus(newStatus: AlertStatus): void,
    attachments: File[],
    setAttachments(files: File[]): void,
    onFileValidationChange?: (isValid: boolean) => void // new prop
}

const NewReview = ({comment, setComment, newStatus, setNewStatus, attachments, setAttachments, onFileValidationChange}: NewReviewProps) => {
    const [isActive, setIsActive] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [uploadError, setUploadError] = React.useState<string | null>(null);
    const [fileValidationSuccess, setFileValidationSuccess] = React.useState<boolean>(false);
    const [hasTriedFileUpload, setHasTriedFileUpload] = React.useState(false);

    const alertStatuses: Array<{
            name: string,
            value: AlertStatus
        }> = [{
            name: 'Open',
            value: 'open'
        }, {
            name: 'Watch',
            value: 'watch'
        }, {
            name: 'Investigating',
            value: 'investig'
        }, {
            name: 'Critical',
            value: 'critical'
        }, {
            name: 'Explained',
            value: 'explained'
        }, {
            name: 'Closed',
            value: 'closed'
        }];
    
    const validateFile = (file: File, maxSize = 15 * 1024 * 1024): string | null => {
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'docx', 'xlsx'];
        const allowedMimeTypes = [
            'image/jpeg',
            'image/png',
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ];
        const invalidChars = /[<>:"/\\|?*\x00-\x1F]/;
        const filename = file.name;
        const fileExtension = filename.split('.').pop()?.toLowerCase();

        if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
            return `Unsupported file extension: ${fileExtension}`;
        }
        if (invalidChars.test(filename)) {
            return `File name contains invalid characters: ${filename}`;
        }
        if (filename.trim().length === 0 || filename.length > 255) {
            return 'File name is empty or too long';
        }
        if (!allowedMimeTypes.includes(file.type)) {
            return `Invalid MIME type: ${file.type}`;
        }
        if (file.size === 0) {
            return 'File is empty';
        }
        if (file.size > maxSize) {
            return `File size exceeds limit: ${(maxSize / 1024 / 1024).toFixed(2)} MB`;
        }
        return null;
    };

    React.useEffect(() => {
        // Only allow Save if: (no files and user has not tried upload) OR (files and all valid)
        // If user has tried to upload and there are no valid files, Save should be disabled
        let canSave = false;
        if (attachments.length === 0 && !hasTriedFileUpload) {
            canSave = true;
        } else if (attachments.length > 0 && fileValidationSuccess) {
            canSave = true;
        } else if (attachments.length === 0 && hasTriedFileUpload) {
            canSave = false;
        } else {
            canSave = false;
        }
        if (onFileValidationChange) {
            onFileValidationChange(canSave);
        }
        // eslint-disable-next-line
    }, [fileValidationSuccess, attachments, hasTriedFileUpload]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setHasTriedFileUpload(true);
        if (event.target.files) {
            const files = Array.from(event.target.files);
            const errors: string[] = [];
            const validFiles: File[] = [];
            files.forEach(file => {
                const error = validateFile(file);
                if (error) {
                    errors.push(`${file.name}: ${error}`);
                } else {
                    validFiles.push(file);
                }
            });
            if (errors.length > 0) {
                setUploadError(errors.join('\n'));
                setFileValidationSuccess(false);
            } else if (validFiles.length > 0) {
                setUploadError(null);
                setFileValidationSuccess(true);
            } else {
                setUploadError(null);
                setFileValidationSuccess(false);
            }
            setAttachments(validFiles);
        }
    };

    const removeFile = (idx: number) => {
        const newFiles = attachments.filter((_, i) => i !== idx);
        setAttachments(newFiles);
        if (newFiles.length === 0) setHasTriedFileUpload(false);
    };

    return (
        <div className="NewReview">
            <div className='AlertStatusRadioList'>
                {alertStatuses.map(({ name, value }) => (
                    <div
                        key={value}
                        className='AlertStatusRadioWrapper'
                        onClick={() => setNewStatus(value)}
                    >
                        <div
                            className={`AlertStatusRadioButton ${value} ${(value === newStatus) ? 'Selected' : ''}`}
                        >
                            {
                                (value === newStatus)
                                ? (
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className='CheckmarkIcon'
                                    />
                                ) : null
                            }
                        </div>
                        <span className='AlertStatusRadioLabel'>{name}</span>
                    </div>
                ))}
            </div>
            <Form.Control
                as='textarea'
                className={`AlertStatusTextArea${isActive ? ' Active' : ''}`}
                rows={15}
                placeholder='Type comment'
                value={comment}
                onChange={e => setComment(e.target.value)}
                onFocus={() => setIsActive(true)}
                onBlur={() => setIsActive(false)}
            />
            <div className="file-upload-container">
                <label htmlFor="file-upload" className="custom-file-upload" title="Files allowed for upload - jpg, jpeg, png, pdf, docx, xlsx">
                    <i className="fa fa-cloud-upload" /> Select Files
                </label>
                <input
                    id="file-upload"
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                <div className="selected-files-list">
                    {attachments.map((file, idx) => (
                    <div key={idx} className="file-item">
                        <span>{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                        <button className="custom-file-delete" onClick={() => removeFile(idx)}>Remove</button>
                    </div>
                    ))}
                </div>
                {fileValidationSuccess && attachments.length > 0 && (
                    <div className="success-message">All files validated successfully!</div>
                )}
                {uploading && <div className="upload-progress">Uploading files...</div>}
                {uploadError && <div className="error-message">{uploadError}</div>}
            </div>
        </div>
    );
}
export default NewReview;